import { ChevronDownIcon } from "@heroicons/react/outline";
import { Check, Search, TagIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/context/auth-context";
import { cn } from "@/lib/utils";
import { useWorkspaceTags } from "@/queries";

const TAG_LIST_HEIGHT = 280;
const VISIBLE_BATCH_SIZE = 100;

interface TagsFilterProps {
  value: string[];
  onChange: (tags: string[]) => void;
}

const TagsFilter: React.FC<TagsFilterProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [visibleCount, setVisibleCount] = useState(VISIBLE_BATCH_SIZE);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const authContext = useAuth();
  const { workspaceId } = useParams();

  const { data: workspaceTags, refetch } = useWorkspaceTags(authContext?.userToken ?? "", {
    workspaceId: Number(workspaceId!),
  });

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  useEffect(() => {
    if (!isOpen) {
      setInputValue("");
      setVisibleCount(VISIBLE_BATCH_SIZE);
    }
  }, [isOpen]);

  const trimmedQuery = inputValue.trim().toLowerCase();

  const filteredTags = useMemo(() => {
    const allTags = Array.isArray(workspaceTags) ? workspaceTags : [];
    let result = [...allTags];

    if (trimmedQuery) {
      result = result.filter(tag => tag.toLowerCase().includes(trimmedQuery));
    }

    result.sort((a, b) => {
      const aSelected = value.includes(a) ? 0 : 1;
      const bSelected = value.includes(b) ? 0 : 1;
      if (aSelected !== bSelected) {
        return aSelected - bSelected;
      }

      if (trimmedQuery) {
        const aStarts = a.toLowerCase().startsWith(trimmedQuery) ? 0 : 1;
        const bStarts = b.toLowerCase().startsWith(trimmedQuery) ? 0 : 1;
        if (aStarts !== bStarts) {
          return aStarts - bStarts;
        }
      }

      return a.toLowerCase().localeCompare(b.toLowerCase());
    });

    return result;
  }, [workspaceTags, value, trimmedQuery]);

  const visibleTags = useMemo(
    () => filteredTags.slice(0, visibleCount),
    [filteredTags, visibleCount],
  );

  const hasMore = filteredTags.length > visibleCount;

  const handleToggleTag = useCallback(
    (tag: string) => {
      const newTags = value.includes(tag) ? value.filter(t => t !== tag) : [...value, tag];
      onChange(newTags);
    },
    [value, onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || !hasMore) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 80) {
      setVisibleCount(prev => prev + VISIBLE_BATCH_SIZE);
    }
  }, [hasMore]);

  useEffect(() => {
    setVisibleCount(VISIBLE_BATCH_SIZE);
  }, [trimmedQuery]);

  const hasSelection = value.length > 0;

  const getDisplayText = () => {
    if (value.length === 0) {
      return "Tags";
    }
    if (value.length === 1) {
      return value[0];
    }
    return `${value.length} tags`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-8 w-auto min-w-[80px] items-center justify-between gap-1.5 rounded-md border-0 bg-transparent px-2 py-1 text-sm font-medium shadow-none transition-colors hover:bg-gray-100 focus:bg-gray-100 focus:outline-none focus:ring-0",
            hasSelection ? "bg-gray-100 text-gray-900" : "text-gray-600 hover:text-gray-900",
          )}
          aria-label={`Filter by tags${hasSelection ? " (active)" : ""}`}
        >
          <div className="flex items-center gap-1.5">
            <TagIcon className="h-4 w-4" />
            <span className="truncate">{getDisplayText()}</span>
          </div>
          <ChevronDownIcon className="h-3 w-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="max-h-[min(70vh,500px)] w-72 overflow-hidden p-0"
        align="start"
        onOpenAutoFocus={e => {
          e.preventDefault();
          inputRef.current?.focus();
        }}
      >
        <div className="flex min-h-0 flex-col">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <Search className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search tags..."
              className="h-7 w-full border-0 bg-transparent text-sm outline-none placeholder:text-gray-400 focus:ring-0"
            />
          </div>

          <div
            ref={listRef}
            className="min-h-0 shrink-0 overflow-y-auto overscroll-contain"
            style={{ maxHeight: `${TAG_LIST_HEIGHT}px`, scrollbarGutter: "stable" }}
            onScroll={handleScroll}
          >
            {visibleTags.length > 0 ? (
              <div className="py-1">
                {visibleTags.map(tag => {
                  const isSelected = value.includes(tag);
                  return (
                    <button
                      type="button"
                      key={tag}
                      className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors hover:bg-gray-50 active:bg-gray-100"
                      onClick={() => handleToggleTag(tag)}
                    >
                      <span
                        className={cn(
                          "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors",
                          isSelected
                            ? "border-blue-500 bg-blue-500 text-white"
                            : "border-gray-300 bg-white",
                        )}
                      >
                        {isSelected && <Check className="h-3 w-3" strokeWidth={2.5} />}
                      </span>
                      <span className="truncate">{tag}</span>
                    </button>
                  );
                })}

                {hasMore && (
                  <div className="px-3 py-2 text-center text-xs text-gray-400">
                    Showing {visibleCount} of {filteredTags.length} tags — scroll for more
                  </div>
                )}
              </div>
            ) : (
              <div className="px-3 py-6 text-center text-sm text-gray-400">
                {trimmedQuery ? (
                  <>
                    No tags matching &quot;<span className="font-medium">{inputValue.trim()}</span>
                    &quot;
                  </>
                ) : (
                  "No tags available"
                )}
              </div>
            )}
          </div>

          {hasSelection && (
            <div className="flex items-center justify-between border-t border-gray-100 px-3 py-1.5">
              <span className="text-xs text-gray-400">{value.length} selected</span>
              <button
                type="button"
                className="text-xs text-gray-500 transition-colors hover:text-gray-700"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default TagsFilter;
